import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  CheckCircle2,
  Loader2,
  AlertCircle,
  ShieldCheck,
  FileText,
  Eraser,
  PenLine,
  Send,
  ExternalLink,
} from "lucide-react";

// Public route — no auth. Uses a bare axios instance to bypass admin
// interceptors (which redirect to /login on 401).
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const publicApi = axios.create({ baseURL: `${BACKEND_URL}/api`, timeout: 30000 });

// ------ Signature canvas ------
function SignatureCanvas({ onChange, testid = "signature-canvas" }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const [isEmpty, setIsEmpty] = useState(true);

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const t = e.touches?.[0];
    const clientX = t ? t.clientX : e.clientX;
    const clientY = t ? t.clientY : e.clientY;
    return {
      x: (clientX - rect.left) * (canvasRef.current.width / rect.width),
      y: (clientY - rect.top) * (canvasRef.current.height / rect.height),
    };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    last.current = getPos(e);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = getPos(e);
    ctx.strokeStyle = "#0f172a";
    ctx.lineWidth = 2.4;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    if (isEmpty) setIsEmpty(false);
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    if (canvasRef.current) {
      onChange(canvasRef.current.toDataURL("image/png"));
    }
  };

  const clear = () => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    setIsEmpty(true);
    onChange("");
  };

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    // Retina-friendly size
    const dpr = window.devicePixelRatio || 1;
    c.width = c.clientWidth * dpr;
    c.height = c.clientHeight * dpr;
    // V1.38.1 · Canvas transparente (no rellenamos con blanco): así el PNG
    // exportado por toDataURL conserva el canal alpha y no oculta el texto
    // subyacente al superponerlo en los PDF.
  }, []);

  return (
    <div>
      <div className="relative rounded-xl border-2 border-dashed border-orange-300 bg-orange-50/40 overflow-hidden">
        <canvas
          ref={canvasRef}
          data-testid={testid}
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
          className="block w-full h-44 sm:h-52 touch-none cursor-crosshair"
        />
        {isEmpty && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-orange-400 text-sm font-medium">
            Firma aquí con el dedo o el ratón
          </div>
        )}
      </div>
      <div className="flex justify-end mt-2">
        <button
          type="button"
          onClick={clear}
          data-testid="signature-clear"
          className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-orange-600 transition-colors"
        >
          <Eraser className="h-3.5 w-3.5" />
          Borrar y volver a firmar
        </button>
      </div>
    </div>
  );
}

// ------ Main Page ------
export default function SignaturePage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [error, setError] = useState("");
  const [pdfUrl, setPdfUrl] = useState("");

  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [debugOtp, setDebugOtp] = useState("");

  const [signatureImg, setSignatureImg] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [completedHash, setCompletedHash] = useState("");

  const loadInfo = useCallback(async () => {
    setLoadingInfo(true);
    setError("");
    try {
      const { data } = await publicApi.get(`/public/signature/${token}`);
      setInfo(data);
      if (data.signed_at) setCompleted(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "El enlace de firma no es válido o ha caducado.");
    } finally {
      setLoadingInfo(false);
    }
  }, [token]);

  useEffect(() => { loadInfo(); }, [loadInfo]);

  // Fetch PDF preview as blob (once info is loaded)
  useEffect(() => {
    if (!info) return;
    let cancelled = false;
    let url = "";
    (async () => {
      try {
        const res = await publicApi.get(`/public/signature/${token}/preview.pdf`, {
          responseType: "blob",
        });
        if (cancelled) return;
        url = window.URL.createObjectURL(res.data);
        setPdfUrl(url);
      } catch {
        /* non-blocking */
      }
    })();
    return () => {
      cancelled = true;
      if (url) window.URL.revokeObjectURL(url);
    };
  }, [info, token]);

  const requireOtp = info?.require_otp !== false;
  // V1.33-X · Canales permitidos según el método de firma configurado.
  const otpChannels = info?.otp_channels ?? (requireOtp ? ["email", "sms"] : []);
  const [channel, setChannel] = useState("");
  const activeChannel = channel || otpChannels[0] || "sms";
  const canSign = useMemo(() => {
    if (submitting) return false;
    if (!signatureImg) return false;
    if (requireOtp && !otpVerified) return false;
    return true;
  }, [submitting, signatureImg, requireOtp, otpVerified]);

  const sendOtp = async () => {
    setSendingOtp(true);
    try {
      const { data } = await publicApi.post(`/public/signature/${token}/send-otp`, { channel: activeChannel });
      setOtpSent(true);
      setOtpVerified(false);
      // MOCKED — remove when real SMS integration lands.
      if (data.debug_otp) setDebugOtp(data.debug_otp);
      toast.success("Código enviado. Revisa tu WhatsApp o SMS.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo enviar el código.");
    } finally {
      setSendingOtp(false);
    }
  };

  const verifyOtp = async () => {
    if (!otp.trim()) {
      toast.error("Introduce el código recibido.");
      return;
    }
    setVerifyingOtp(true);
    try {
      await publicApi.post(`/public/signature/${token}/verify-otp`, { otp: otp.trim() });
      setOtpVerified(true);
      toast.success("Código verificado.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Código incorrecto.");
    } finally {
      setVerifyingOtp(false);
    }
  };

  const submitSignature = async () => {
    if (!canSign) return;
    setSubmitting(true);
    try {
      const { data } = await publicApi.post(`/public/signature/${token}/sign`, {
        otp: otp.trim(),
        signature_image_b64: signatureImg,
      });
      setCompleted(true);
      setCompletedHash(data.document_hash || "");
      toast.success("Firma registrada correctamente.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo registrar la firma.");
    } finally {
      setSubmitting(false);
    }
  };

  // ------ Renders ------
  if (loadingInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <Loader2 className="h-6 w-6 animate-spin text-orange-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 px-4">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-lg p-6 sm:p-8 text-center" data-testid="signature-error">
          <div className="inline-flex items-center gap-2 rounded-full bg-red-100 text-red-800 text-xs font-semibold px-3 py-1 mb-3">
            <AlertCircle className="h-3.5 w-3.5" />
            Enlace no válido
          </div>
          <h1 className="text-2xl font-bold mb-3">No se puede abrir la firma</h1>
          <p className="text-neutral-600">{error}</p>
        </div>
      </div>
    );
  }

  if (info.status === "expired" || info.status === "cancelled") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 px-4">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-lg p-6 sm:p-8 text-center" data-testid="signature-inactive">
          <div className="inline-flex items-center gap-2 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold px-3 py-1 mb-3">
            <AlertCircle className="h-3.5 w-3.5" />
            {info.status === "expired" ? "Enlace caducado" : "Solicitud cancelada"}
          </div>
          <h1 className="text-2xl font-bold mb-3">
            {info.status === "expired"
              ? "El enlace ha caducado"
              : "Esta solicitud fue cancelada"}
          </h1>
          <p className="text-neutral-600">
            Contacta con la autoescuela {info.autoescuela?.nombre} para obtener un nuevo enlace.
          </p>
          {info.autoescuela?.telefono && (
            <p className="mt-3 text-sm font-semibold text-orange-600">{info.autoescuela.telefono}</p>
          )}
        </div>
      </div>
    );
  }

  if (completed || info.status === "signed") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 px-4 py-10">
        <div className="max-w-lg w-full bg-white rounded-3xl shadow-lg p-6 sm:p-8 text-center" data-testid="signature-success">
          <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100 text-emerald-800 text-xs font-semibold px-3 py-1 mb-3">
            <CheckCircle2 className="h-3.5 w-3.5" />
            Firma completada
          </div>
          <h1 className="text-2xl font-bold mb-3">¡Contrato firmado!</h1>
          <p className="text-neutral-600 leading-relaxed">
            Gracias, <b>{info.signer?.name}</b>. Hemos registrado tu firma en el contrato{" "}
            <b className="font-mono">Nº {info.contract?.numero}</b>. Recibirás una copia por WhatsApp
            en breve.
          </p>
          {completedHash && (
            <div className="mt-5 text-[10px] text-neutral-400 break-all font-mono">
              Huella digital del documento: {completedHash}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Active signing flow
  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="max-w-3xl mx-auto px-4 py-6 sm:py-10 space-y-5">
        {/* Header card */}
        <div className="bg-white rounded-3xl shadow-sm border border-neutral-100 p-5 sm:p-7">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-orange-600 font-bold">
            <ShieldCheck className="h-3.5 w-3.5" />
            Firma segura · {info.autoescuela?.nombre}
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold leading-tight mt-2">
            Firma tu contrato de enseñanza
          </h1>
          <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
            <div className="rounded-xl bg-neutral-50 border border-neutral-200 px-3 py-2">
              <div className="text-[11px] uppercase tracking-wider text-neutral-500 font-bold">Contrato</div>
              <div className="font-mono font-bold" data-testid="sig-contract-numero">Nº {info.contract?.numero}</div>
            </div>
            <div className="rounded-xl bg-neutral-50 border border-neutral-200 px-3 py-2">
              <div className="text-[11px] uppercase tracking-wider text-neutral-500 font-bold">Firmante</div>
              <div className="font-semibold text-neutral-800 truncate" data-testid="sig-signer-name">{info.signer?.name}</div>
            </div>
          </div>
        </div>

        {/* PDF Preview */}
        <div className="bg-white rounded-3xl shadow-sm border border-neutral-100 p-4 sm:p-5" data-testid="signature-pdf-preview">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="h-4 w-4 text-orange-500" />
            <h2 className="text-sm font-bold uppercase tracking-wider text-neutral-500">
              Documento a firmar
            </h2>
            {pdfUrl && (
              <a
                href={pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-auto inline-flex items-center gap-1 text-xs text-orange-600 hover:text-orange-700 font-semibold"
                data-testid="sig-open-pdf"
              >
                Abrir PDF
                <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
          {pdfUrl ? (
            <iframe
              src={pdfUrl}
              title="Contrato en borrador"
              className="w-full h-[420px] sm:h-[560px] rounded-xl border border-neutral-200 bg-white"
              data-testid="sig-pdf-iframe"
            />
          ) : (
            <div className="h-[420px] flex items-center justify-center rounded-xl border border-dashed border-neutral-200 text-sm text-neutral-400">
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Cargando previsualización…
            </div>
          )}
          <p className="text-[11px] text-neutral-500 mt-2">
            Este documento se marca como <b>BORRADOR</b> hasta que todas las firmas se completen. Cuando
            firmes, el contrato quedará bloqueado y no podrá modificarse.
          </p>
        </div>

        {/* OTP */}
        {requireOtp && (
          <div className="bg-white rounded-3xl shadow-sm border border-neutral-100 p-5 sm:p-7" data-testid="signature-otp-block">
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck className="h-4 w-4 text-orange-500" />
              <h2 className="text-sm font-bold uppercase tracking-wider text-neutral-500">
                Verificación por código
              </h2>
              {otpVerified && (
                <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-bold px-2 py-0.5">
                  <CheckCircle2 className="h-3 w-3" /> Verificado
                </span>
              )}
            </div>
            {!otpSent ? (
              <div className="space-y-3">
                {otpChannels.length > 1 && (
                  <div className="flex gap-2" data-testid="sig-channel-picker">
                    {otpChannels.map((ch) => (
                      <button
                        key={ch}
                        type="button"
                        onClick={() => setChannel(ch)}
                        data-testid={`sig-channel-${ch}`}
                        className={
                          "px-3 py-1.5 rounded-full text-xs font-bold border " +
                          (activeChannel === ch
                            ? "bg-orange-500 text-white border-orange-500"
                            : "bg-white text-neutral-600 border-neutral-200 hover:border-orange-300")
                        }
                      >
                        {ch === "email" ? "📧 Email" : "📱 SMS"}
                      </button>
                    ))}
                  </div>
                )}
                <div className="flex flex-col sm:flex-row gap-3 items-start">
                  <p className="text-sm text-neutral-600 flex-1">
                    Pulsa el botón para recibir un código de un solo uso{" "}
                    {activeChannel === "email"
                      ? <>en el email <b>{info.signer?.email || "registrado"}</b></>
                      : <>al teléfono <b>{info.signer?.phone || "registrado"}</b></>}.
                  </p>
                  <button
                    type="button"
                    onClick={sendOtp}
                    disabled={sendingOtp}
                    data-testid="sig-send-otp"
                    className="h-11 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-bold px-5 inline-flex items-center gap-2 shadow-sm disabled:opacity-50 whitespace-nowrap"
                  >
                    {sendingOtp ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    Enviar código
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-neutral-600">
                  Introduce el código de 6 dígitos recibido en{" "}
                  <b>{activeChannel === "email" ? info.signer?.email : info.signer?.phone}</b>.
                </p>
                {debugOtp && (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 text-amber-900 px-3 py-2 text-xs">
                    <b>Modo desarrollo:</b> código de prueba{" "}
                    <span className="font-mono font-bold">{debugOtp}</span>
                    <div className="text-[10px] text-amber-700 mt-1">Este aviso desaparecerá cuando SMS/Email esté integrado.</div>
                  </div>
                )}
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    autoComplete="one-time-code"
                    value={otp}
                    disabled={otpVerified}
                    onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, ""))}
                    data-testid="sig-otp-input"
                    className="flex-1 h-12 rounded-xl border border-neutral-200 bg-white px-4 text-lg font-mono tracking-widest text-center focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:bg-neutral-100"
                    placeholder="••••••"
                  />
                  <button
                    type="button"
                    onClick={verifyOtp}
                    disabled={verifyingOtp || otpVerified || otp.length !== 6}
                    data-testid="sig-verify-otp"
                    className="h-12 rounded-xl bg-neutral-900 hover:bg-neutral-800 text-white font-bold px-5 inline-flex items-center justify-center gap-2 disabled:opacity-50 whitespace-nowrap"
                  >
                    {verifyingOtp ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                    Verificar
                  </button>
                </div>
                {!otpVerified && (
                  <button
                    type="button"
                    onClick={sendOtp}
                    disabled={sendingOtp}
                    className="text-xs text-neutral-500 hover:text-orange-600 transition-colors"
                    data-testid="sig-resend-otp"
                  >
                    Volver a enviar el código
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Signature */}
        <div className="bg-white rounded-3xl shadow-sm border border-neutral-100 p-5 sm:p-7" data-testid="signature-canvas-block">
          <div className="flex items-center gap-2 mb-3">
            <PenLine className="h-4 w-4 text-orange-500" />
            <h2 className="text-sm font-bold uppercase tracking-wider text-neutral-500">
              Firma manuscrita
            </h2>
          </div>
          <SignatureCanvas onChange={setSignatureImg} />
          <p className="text-[11px] text-neutral-500 mt-3">
            Al firmar declaras haber leído y aceptado íntegramente el contrato mostrado arriba,
            así como las condiciones económicas, de desistimiento y protección de datos.
          </p>

          <button
            type="button"
            onClick={submitSignature}
            disabled={!canSign}
            data-testid="sig-submit"
            className="mt-5 w-full h-14 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-base inline-flex items-center justify-center gap-2 shadow-sm disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />}
            Firmar y aceptar el contrato
          </button>
          {!canSign && !submitting && (
            <p className="text-[11px] text-neutral-500 mt-2 text-center">
              {requireOtp && !otpVerified
                ? "Verifica el código antes de firmar."
                : "Firma en el recuadro anterior para continuar."}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
